import { Download, X } from "lucide-react";
import { useEffect, useState } from "react";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../lib/appLinks";
import { useLocalization, type TranslationKey } from "../../lib/i18n";
import { getStoreBadgeAssets } from "./StoreBadges";

export const IOS_APP_PROMPT_DISMISSED_KEY = "sportex100:iosAppPromptDismissed";
export const ANDROID_APP_PROMPT_DISMISSED_KEY = "sportex100:androidAppPromptDismissed";
export const APP_PROMPT_CONTEXT_KEY = "pendingAppPrompt";
export const IOS_APP_PROMPT_CONTEXT_KEY = APP_PROMPT_CONTEXT_KEY;

type AppPromptPlatform = "ios" | "android";

type PromptCopy = {
  actionKey: TranslationKey;
  bodyKey: TranslationKey;
  dismissedKey: string;
  titleKey: TranslationKey;
  url: string;
};

const promptCopyByPlatform: Record<AppPromptPlatform, PromptCopy> = {
  ios: {
    actionKey: "guest.iosAppPrompt.action",
    bodyKey: "guest.iosAppPrompt.body",
    dismissedKey: IOS_APP_PROMPT_DISMISSED_KEY,
    titleKey: "guest.iosAppPrompt.title",
    url: APP_STORE_URL,
  },
  android: {
    actionKey: "guest.androidAppPrompt.action",
    bodyKey: "guest.androidAppPrompt.body",
    dismissedKey: ANDROID_APP_PROMPT_DISMISSED_KEY,
    titleKey: "guest.androidAppPrompt.title",
    url: PLAY_STORE_URL,
  },
};

function isStandaloneWebApp() {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(
    (window.navigator as Navigator & { standalone?: boolean }).standalone ||
      window.matchMedia?.("(display-mode: standalone)").matches,
  );
}

export function getAppDownloadPromptPlatform(): AppPromptPlatform | null {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return null;
  }

  if (isStandaloneWebApp()) {
    return null;
  }

  const userAgent = navigator.userAgent;
  const isAppleTouchDevice =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isAppleTouchDevice) {
    return "ios";
  }

  if (/Android/i.test(userAgent)) {
    return "android";
  }

  return null;
}

export function isIOSWeb() {
  return getAppDownloadPromptPlatform() === "ios";
}

export function isAndroidWeb() {
  return getAppDownloadPromptPlatform() === "android";
}

export function AppDownloadPrompt() {
  const { locale, t } = useLocalization();
  const [platform, setPlatform] = useState<AppPromptPlatform | null>(null);

  useEffect(() => {
    const detectedPlatform = getAppDownloadPromptPlatform();
    if (!detectedPlatform) {
      return;
    }

    const promptCopy = promptCopyByPlatform[detectedPlatform];
    if (window.localStorage.getItem(promptCopy.dismissedKey) === "1") {
      return;
    }

    setPlatform(detectedPlatform);
  }, []);

  if (!platform) {
    return null;
  }

  const promptCopy = promptCopyByPlatform[platform];
  const badges = getStoreBadgeAssets(locale);
  const badgeSrc = platform === "ios" ? badges.appStore : badges.googlePlay;

  const handleDismiss = () => {
    window.localStorage.setItem(promptCopy.dismissedKey, "1");
    window.sessionStorage.removeItem(APP_PROMPT_CONTEXT_KEY);
    setPlatform(null);
  };

  return (
    <div className="rounded-lg border border-white/15 bg-black/70 px-4 py-3 text-white shadow-card backdrop-blur">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-[#F5A623]">
          <Download className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">{t(promptCopy.titleKey)}</p>
          <p className="mt-1 text-xs font-medium leading-5 text-white/72">{t(promptCopy.bodyKey)}</p>
          <a
            href={promptCopy.url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex transition-opacity hover:opacity-90"
            aria-label={t(promptCopy.actionKey)}
          >
            <img
              src={badgeSrc}
              alt={t(promptCopy.actionKey)}
              className="h-10 w-auto max-w-full object-contain"
              loading="lazy"
            />
          </a>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white/72 transition-colors hover:bg-white/10 hover:text-white"
          aria-label={t("guest.appPrompt.dismiss")}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function IosAppDownloadPrompt() {
  return <AppDownloadPrompt />;
}
